/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { EditorThemeClasses } from 'lexical';

import './TwygsEditorTheme.css';
import { BeautifulMentionsTheme } from 'lexical-beautiful-mentions';

const beautifulMentionsTheme: BeautifulMentionsTheme = {
  "@": {
    container: "cursor-pointer px-2 py-0.5 mx-0.5 rounded-md bg-growth-green-50 text-growth-green-900 font-medium " +
      "shadow-[2px_2px_4px_rgba(0,0,0,0.05),-2px_-2px_4px_rgba(255,255,255,0.8)] " +
      "border border-growth-green-100 transition-all duration-200",
    containerFocused: "cursor-pointer px-2 py-0.5 mx-0.5 rounded-md bg-growth-green-100 text-growth-green-900 font-medium " +
      "shadow-[inset_2px_2px_4px_rgba(0,0,0,0.05),inset_-2px_-2px_4px_rgba(255,255,255,0.8)] " +
      "border border-growth-green-200 outline-none transition-all duration-200",
    trigger: "text-growth-green-500 font-semibold",
    value: "text-growth-green-700"
  },
  "#": {
    container: "cursor-pointer px-2 py-0.5 mx-0.5 rounded-md bg-blue-50 text-blue-700 font-medium " +
      "shadow-[2px_2px_4px_rgba(0,0,0,0.05),-2px_-2px_4px_rgba(255,255,255,0.8)] " +
      "border border-blue-100 transition-all duration-200",
    containerFocused: "cursor-pointer px-2 py-0.5 mx-0.5 rounded-md bg-blue-100 text-blue-800 font-medium " +
      "shadow-[inset_2px_2px_4px_rgba(0,0,0,0.05),inset_-2px_-2px_4px_rgba(255,255,255,0.8)] " +
      "border border-blue-200 outline-none transition-all duration-200",
    trigger: "text-blue-500 font-semibold",
    value: "text-blue-700"
  }
};


const getTheme = (isEditable: boolean): EditorThemeClasses => ({
  isEditable,
  autocomplete: 'TwygsEditorTheme__autocomplete',
  blockCursor: 'TwygsEditorTheme__blockCursor',
  characterLimit: 'TwygsEditorTheme__characterLimit',
  code: 'TwygsEditorTheme__code',
  beautifulMentions: beautifulMentionsTheme,
  codeHighlight: {
    atrule: 'TwygsEditorTheme__tokenAttr',
    attr: 'TwygsEditorTheme__tokenAttr',
    boolean: 'TwygsEditorTheme__tokenProperty',
    builtin: 'TwygsEditorTheme__tokenSelector',
    cdata: 'TwygsEditorTheme__tokenComment',
    char: 'TwygsEditorTheme__tokenSelector',
    class: 'TwygsEditorTheme__tokenFunction',
    'class-name': 'TwygsEditorTheme__tokenFunction',
    comment: 'TwygsEditorTheme__tokenComment',
    constant: 'TwygsEditorTheme__tokenProperty',
    deleted: 'TwygsEditorTheme__tokenProperty',
    doctype: 'TwygsEditorTheme__tokenComment',
    entity: 'TwygsEditorTheme__tokenOperator',
    function: 'TwygsEditorTheme__tokenFunction',
    important: 'TwygsEditorTheme__tokenVariable',
    inserted: 'TwygsEditorTheme__tokenSelector',
    keyword: 'TwygsEditorTheme__tokenAttr',
    namespace: 'TwygsEditorTheme__tokenVariable',
    number: 'TwygsEditorTheme__tokenProperty',
    operator: 'TwygsEditorTheme__tokenOperator',
    prolog: 'TwygsEditorTheme__tokenComment',
    property: 'TwygsEditorTheme__tokenProperty',
    punctuation: 'TwygsEditorTheme__tokenPunctuation',
    regex: 'TwygsEditorTheme__tokenVariable',
    selector: 'TwygsEditorTheme__tokenSelector',
    string: 'TwygsEditorTheme__tokenSelector',
    symbol: 'TwygsEditorTheme__tokenProperty',
    tag: 'TwygsEditorTheme__tokenProperty',
    url: 'TwygsEditorTheme__tokenOperator',
    variable: 'TwygsEditorTheme__tokenVariable'
  },
  embedBlock: {
    base: 'TwygsEditorTheme__embedBlock',
    focus: 'TwygsEditorTheme__embedBlockFocus'
  },
  hashtag: 'TwygsEditorTheme__hashtag',
  heading: {
    h1: 'TwygsEditorTheme__h1',
    h2: 'TwygsEditorTheme__h2',
    h3: 'TwygsEditorTheme__h3',
    h4: 'TwygsEditorTheme__h4',
    h5: 'TwygsEditorTheme__h5',
    h6: 'TwygsEditorTheme__h6'
  },
  hr: 'TwygsEditorTheme__hr',
  media: 'editor-media',
  indent: 'TwygsEditorTheme__indent',
  inlineMedia: 'inline-editor-media',
  layoutContainer: 'TwygsEditorTheme__layoutContainer',
  layoutItem: 'TwygsEditorTheme__layoutItem',
  link: 'TwygsEditorTheme__link',
  list: {
    checklist: 'TwygsEditorTheme__checklist',
    listitem: 'TwygsEditorTheme__listItem',
    listitemChecked: 'TwygsEditorTheme__listItemChecked',
    listitemUnchecked: 'TwygsEditorTheme__listItemUnchecked',
    nested: {
      listitem: 'TwygsEditorTheme__nestedListItem'
    },
    olDepth: [
      'TwygsEditorTheme__ol1',
      'TwygsEditorTheme__ol2',
      'TwygsEditorTheme__ol3',
      'TwygsEditorTheme__ol4',
      'TwygsEditorTheme__ol5'
    ],
    ul: 'TwygsEditorTheme__ul'
  },
  ltr: isEditable ? 'TwygsEditorTheme__ltr' : 'TwygsViewerTheme__ltr',
  mark: 'TwygsEditorTheme__mark',
  markOverlap: 'TwygsEditorTheme__markOverlap',
  paragraph: 'TwygsEditorTheme__paragraph',
  quote: 'TwygsEditorTheme__quote',
  rtl: 'TwygsEditorTheme__rtl',
  table: 'TwygsEditorTheme__table',
  tableCell: 'TwygsEditorTheme__tableCell',
  tableCellActionButton: 'TwygsEditorTheme__tableCellActionButton',
  tableCellActionButtonContainer: 'TwygsEditorTheme__tableCellActionButtonContainer',
  tableCellEditing: 'TwygsEditorTheme__tableCellEditing',
  tableCellHeader: 'TwygsEditorTheme__tableCellHeader',
  tableCellPrimarySelected: 'TwygsEditorTheme__tableCellPrimarySelected',
  tableCellResizer: 'TwygsEditorTheme__tableCellResizer',
  tableCellSelected: 'TwygsEditorTheme__tableCellSelected',
  tableCellSortedIndicator: 'TwygsEditorTheme__tableCellSortedIndicator',
  tableResizeRuler: 'TwygsEditorTheme__tableCellResizeRuler',
  tableSelected: 'TwygsEditorTheme__tableSelected',
  tableSelection: 'TwygsEditorTheme__tableSelection',
  text: {
    bold: 'TwygsEditorTheme__textBold',
    code: 'TwygsEditorTheme__textCode',
    italic: 'TwygsEditorTheme__textItalic',
    strikethrough: 'TwygsEditorTheme__textStrikethrough',
    subscript: 'TwygsEditorTheme__textSubscript',
    superscript: 'TwygsEditorTheme__textSuperscript',
    underline: 'TwygsEditorTheme__textUnderline',
    underlineStrikethrough: 'TwygsEditorTheme__textUnderlineStrikethrough'
  }
});

export default getTheme;
